import React from 'react';
import { type MessageDescriptor, useIntl } from 'react-intl-next';
import { defineMessages } from 'react-intl-next';

import ChartTrendIcon from '@atlaskit/icon/core/chart-trend';
import CommentIcon from '@atlaskit/icon/core/comment';
import LockLoadedIcon from '@atlaskit/icon/core/lock-locked';
import PageIcon from '@atlaskit/icon/core/page';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import PersonAddIcon from '@atlaskit/icon/core/person-add';
import { Flex, Stack, xcss } from '@atlaskit/primitives';

import { FeatureRow } from './feature-row';

const messages = defineMessages({
	unlimitedPages: {
		id: 'experiment-aua-improve-conversion.feature-name-column.unlimited-pages',
		defaultMessage: 'Unlimited pages',
		description: 'The name of a feature available in a paid edition of Confluence.',
	},
	unlimitedUsers: {
		id: 'experiment-aua-improve-conversion.feature-name-column.unlimited-users',
		defaultMessage: 'Unlimited users',
		description: 'The name of a feature available in a paid edition of Confluence.',
	},
	pagePermissions: {
		id: 'experiment-aua-improve-conversion.feature-name-column.page-permissions',
		defaultMessage: 'Page permissions',
		description: 'The name of a feature available in a paid edition of Confluence.',
	},
	freeGuestAccess: {
		id: 'experiment-aua-improve-conversion.feature-name-column.free-guest-access',
		defaultMessage: 'Free guest access',
		description: 'The name of a feature available in a paid edition of Confluence.',
	},
	pageInsights: {
		id: 'experiment-aua-improve-conversion.feature-name-column.page-insights',
		defaultMessage: 'Page insights',
		description: 'The name of a feature available in a paid edition of Confluence.',
	},
	atlassianSupport: {
		id: 'experiment-aua-improve-conversion.feature-name-column.atlassian-support',
		defaultMessage: 'Atlassian support',
		description: 'The name of a feature available in a paid edition of Confluence.',
	},
});

type Feature = {
	key:
		| 'unlimitedPages'
		| 'unlimitedUsers'
		| 'pagePermissions'
		| 'freeGuestAccess'
		| 'pageInsights'
		| 'atlassianSupport';
	icon: JSX.Element;
	name: MessageDescriptor;
};

export const FeatureNameColumn = () => {
	const { formatMessage } = useIntl();

	return (
		<Flex xcss={featureNameColumnStyles} justifyContent="center">
			<Stack space="space.200">
				{features.map(({ key, icon, name }: Feature) => (
					<FeatureRow key={key} name={formatMessage(name)} icon={icon} />
				))}
			</Stack>
		</Flex>
	);
};

const features: Feature[] = [
	{
		key: 'unlimitedPages',
		icon: <PageIcon label="" />,
		name: messages.unlimitedPages,
	},
	{
		key: 'unlimitedUsers',
		icon: <PersonAddIcon label="" />,
		name: messages.unlimitedUsers,
	},
	{
		key: 'pagePermissions',
		icon: <LockLoadedIcon label="" />,
		name: messages.pagePermissions,
	},
	{
		key: 'freeGuestAccess',
		icon: <PeopleGroupIcon label="" />,
		name: messages.freeGuestAccess,
	},
	{
		key: 'pageInsights',
		icon: <ChartTrendIcon label="" />,
		name: messages.pageInsights,
	},
	{
		key: 'atlassianSupport',
		icon: <CommentIcon label="" />,
		name: messages.atlassianSupport,
	},
];

const featureNameColumnStyles = xcss({
	width: '210px',
	height: '295px',
	backgroundColor: 'color.background.input',
	paddingTop: 'space.300',
});
